import type { ApiError } from '@barbeiropro/contracts';

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.message);
  }
  get code() {
    return this.body.code;
  }
  /** Mensagem de erro por campo (para formulários). */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.body.issues ?? []).map((i) => [i.path, i.message]));
  }
}

function csrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)bp_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : '';
}

let refreshing: Promise<boolean> | null = null;
async function tryRefresh() {
  refreshing ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => setTimeout(() => (refreshing = null), 0));
  return refreshing;
}

type Query = Record<string, string | number | boolean | null | undefined | string[]>;

export function qs(query?: Query) {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => p.append(k, x));
    else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; query?: Query; headers?: Record<string, string>; retry?: boolean; raw?: boolean } = {},
): Promise<T> {
  const isForm = opts.body instanceof FormData;
  const headers: Record<string, string> = { ...opts.headers };
  if (opts.body !== undefined && !isForm) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken();

  const res = await fetch(`/api${path}${qs(opts.query)}`, {
    method,
    credentials: 'include',
    headers,
    body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
  });

  if (res.status === 401 && opts.retry !== false && !path.startsWith('/auth/')) {
    if (await tryRefresh()) return request<T>(method, path, { ...opts, retry: false });
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ statusCode: res.status, code: 'error', message: 'Erro inesperado' }))) as ApiError;
    throw new HttpError(res.status, body);
  }
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>('POST', path, { body: body ?? {}, headers }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body: body ?? {} }),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, { body }),
  /** Baixa um arquivo (ex.: export XLSX) respeitando os filtros da query. */
  async download(path: string, query: Query | undefined, filename: string) {
    const res = await request<Response>('GET', path, { query, raw: true });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: filename });
    a.click();
    URL.revokeObjectURL(url);
  },
};

export function errorMessage(e: unknown) {
  if (e instanceof HttpError) return e.message;
  return 'Não foi possível concluir. Verifique a conexão e tente novamente.';
}
